/**
 * Sidebar — composition shell.
 *
 * Phase E1 split: data (projects + direct chats + event bus) lives in
 * `sidebar/useSidebarData`, search in `useSidebarSearch`, user profile
 * in `useSidebarProfile`. Presentational sub-components live under
 * `sidebar/`: NavGroups, AssistantSubmenu, SidebarFooter, SearchModal,
 * ContextMenu, ConfirmDeleteModals. This file only owns the shared
 * UI state (expanded menus, tooltip, rename drafts, context-menu
 * target) and the top-level chrome (brand, tooltip, edge hover zone,
 * create/edit project modals).
 */

import { useEffect, useMemo, useRef, useState, type UIEventHandler } from 'react';
import { PanelLeft } from 'lucide-react';
import { emit as emitAppEvent } from '../events/bus';
import ProjectEditModal from '../../modules/chat/ProjectEditModal';
import UserMenu from '../../modules/user/UserMenu/UserMenu';
// Slice 86: TabType + TAB_METADATA come from the navigation SSOT
// (`./tabMetadata`). The re-exports from `useNavigationState.ts` have
// been retired (the hook owns the hook, not the type vocabulary). The
// previously-imported `LEGACY_TO_PRIMARY` / `PRIMARY_TO_LEGACY` symbol
// pair is no longer used at module scope — the only call sites that
// needed it are now inline lookups via TAB_METADATA (one in the
// rail-flag ON branch, below).
import {
    TAB_METADATA,
    isPrimaryRailKey,
    type TabType,
    type PrimaryRailKey,
} from '../../core/navigation/tabMetadata';
import { type BackendUserWithPlan } from '../../userService';
import NavGroups from './sidebar/NavGroups';
import AssistantSubmenu from './sidebar/AssistantSubmenu';
import SidebarFooter from './sidebar/SidebarFooter';
import SearchModal from './sidebar/SearchModal';
import ContextMenu from './sidebar/ContextMenu';
import ConfirmDeleteModals from './sidebar/ConfirmDeleteModals';
import { useSidebarData } from './sidebar/useSidebarData';
import { useSidebarProfile } from './sidebar/useSidebarProfile';
import { useSidebarSearch } from './sidebar/useSidebarSearch';
import type { ContextMenuTarget, Project } from './sidebar/types';
import { useFlag } from '../flags/useFlag';
import { useTelemetry } from '../telemetry/useTelemetry';
import SidebarPrimaryAdapter from './SidebarPrimaryAdapter';
// Slice 82: the inline LEGACY_TO_PRIMARY / PRIMARY_TO_LEGACY tables
// (which previously needed `PrimaryRailKey` as a value-side annotation)
// now live in tabMetadata.ts. PrimaryRailKey itself is no longer
// referenced in this file; the SidebarPrimaryAdapter consumer infers
// the type from the imported maps. Re-introduce the import only if a
// future Sidebar.tsx change types a rail-key callback directly.
import './Sidebar.css';
import logo from '../../assets/images/logo.png';
import logoText from '../../assets/images/logo-text.png';

type SidebarProps = {
    activeTab: TabType;
    onTabChange: (tab: TabType, subTab?: string) => void;
    isOpen: boolean;
    collapsed: boolean;
    onToggleCollapse: () => void;
    educationSubTab?: string | undefined;
    networkingSubTab?: string | undefined;
    userData: BackendUserWithPlan | null;
    onChatSelect?: ((projectId: string, chatId: string) => void) | undefined;
    onNewDirectChat?: (() => void) | undefined;
    selectedChatId?: string | undefined;
    selectedProjectId?: string | undefined;
    mobileOpen?: boolean | undefined;
    onMobileClose?: (() => void) | undefined;
};

const ROLE_LABELS: Record<string, string> = {
    admin: 'Admin', mentor: 'Mentor', mentee: 'Mentee',
};

// ── Telemetry constants (slice-74.5 audit remediation) ─────────────────
//
// Hoisted to mirror the PrimaryRail.tsx TELEMETRY_EVENTS precedent so the
// cohort event name is not a bare string at the call site. The wire format
// (event name + payload field names + values) is preserved verbatim —
// dashboards subscribed to `primary_rail.cohort.observed` keep working.
const TELEMETRY_EVENTS = {
    COHORT_OBSERVED: 'primary_rail.cohort.observed',
} as const;

// Typed cohort variant. The string values MUST serialize to exactly
// 'new' / 'legacy' — these are the dashboard-side discriminator values
// and changing them would silently break the slice-75 ramp metrics.
const COHORT_VARIANT = {
    NEW: 'new',
    LEGACY: 'legacy',
} as const;
type CohortVariant = typeof COHORT_VARIANT[keyof typeof COHORT_VARIANT];

// ── §9 IA Collapse: key mapping helpers (DERIVED — slice 86) ───────────
//
// Slice 86 retired the `LEGACY_TO_PRIMARY` / `PRIMARY_TO_LEGACY` symbol
// imports — the only call sites that referenced them are the two inline
// lookups in the rail-flag ON branch (the `<SidebarPrimaryAdapter>`
// `activeKey` prop and its `onSelect` handler). Both now read directly
// from the `TAB_METADATA` SSOT via the local helpers below, which
// preserves the slice-81/82 mapping contract byte-for-byte:
//   conversations → conversations  (Direct 1:1 after slice-86 rename)
//   schedule    → schedule         (Direct 1:1)
//   submissions → submissions      (Direct 1:1)
//   insights    → insights         (Direct 1:1)
//   assistant / education / networking → home (fallback)
//   profile     → settings         (fallback)
//
// The slice-74 cohort telemetry (`primary_rail.cohort.observed`) keeps
// emitting identical variant values during the slice-75 ramp — see
// docs/redesign/mentorship-platform-redesign-2026-06-03.md §22.11 / §22.15.

/** Resolve a legacy TabType to its rail-of-record PrimaryRailKey. */
function tabToRailKey(tab: TabType): PrimaryRailKey {
    const row = TAB_METADATA[tab];
    // Why: every TAB_METADATA row carries either `railKey` (rail-visible
    // tabs) or `fallbackRailKey` (non-rail tabs). tabMetadata.ts enforces
    // this at module load, so the non-null assertion below is safe.
    return (row.railKey ?? row.fallbackRailKey)!;
}

/** Resolve a PrimaryRailKey back to its canonical TabType. After
 *  slice 86 every rail key IS a TabType, so the round-trip is a typed
 *  identity — `isPrimaryRailKey` already guarantees membership in the
 *  TabType union via the `PrimaryRailKey extends TabType` constraint. */
function railKeyToTab(key: string): TabType {
    return isPrimaryRailKey(key) ? key : 'home';
}

export default function Sidebar({
    activeTab, onTabChange, isOpen, collapsed, onToggleCollapse,
    networkingSubTab = 'connections', userData,
    onChatSelect, onNewDirectChat, selectedChatId, selectedProjectId, mobileOpen,
}: SidebarProps) {
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['assistant']));
    const [tooltip, setTooltip] = useState<{ top: number; left: number } | null>(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    // Track the menu anchor as state (not a ref) so the UserMenu
    // re-renders + repositions once the DOM node attaches. Reading a
    // ref's `.current` during render is flagged by React 19's rules.
    const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLButtonElement | null>(null);

    const [contextMenu, setContextMenu] = useState<ContextMenuTarget | null>(null);
    const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const [renameProjectValue, setRenameProjectValue] = useState('');
    const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
    const [isYourChatsExpanded, setIsYourChatsExpanded] = useState(true);

    const [confirmDeleteChatId, setConfirmDeleteChatId] = useState<string | null>(null);
    const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<string | null>(null);
    const [confirmDeleteProjectChatId, setConfirmDeleteProjectChatId] =
        useState<{ projectId: string; chatId: string } | null>(null);

    // Why: slice-74 is the observability-prep step of the `primary-rail` ramp.
    // Why: the flag default REMAINS `false` in the registry — slice-74 ships
    //      instrumentation only; the actual flip is deferred to slice-75 once
    //      a dogfooding window has confirmed the success metrics.
    // Why: the legacy NavGroups branch below MUST remain intact and reachable
    //      because the rollback path for slice-75 is exactly "set the flag
    //      back to OFF" — silent regressions there would strand operators.
    // Why: the success metrics for slice-75's flip are the two telemetry
    //      events instrumented in slice-74: `primary_rail.item.click`
    //      (per-click CTR) and `primary_rail.cohort.observed` (per-mount
    //      denominator). Together they let us compute branch-level CTR
    //      without schema gymnastics on the dashboard side.
    const primaryRailEnabled = useFlag('primary-rail');

    // Slice-74 cohort observation. Idempotent: a useRef guard ensures the
    // event fires at most once per Sidebar mount, even if the flag value
    // were to flip mid-session (slice-75 will not do that, but the guard
    // is the cheapest defence-in-depth against a future regression).
    const { track } = useTelemetry();
    const cohortEmittedRef = useRef(false);
    useEffect(() => {
        if (cohortEmittedRef.current) return;
        cohortEmittedRef.current = true;
        const variant: CohortVariant = primaryRailEnabled
            ? COHORT_VARIANT.NEW
            : COHORT_VARIANT.LEGACY;
        track(TELEMETRY_EVENTS.COHORT_OBSERVED, {
            variant,
        });
        // Why: empty deps — one-shot per mount is the contract. Flag changes
        //      within a session must NOT re-fire (would skew the denominator).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const data = useSidebarData({ selectedChatId, onNewDirectChat });
    const profile = useSidebarProfile();
    const search = useSidebarSearch();

    // ── Derived UI state ──────────────────────────────────────────────

    const displayName = userData
        ? (userData.first_name || userData.last_name
            ? `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim()
            : userData.email)
        : (profile.name || 'Loading...');

    const initials = userData
        ? (userData.first_name && userData.last_name
            ? `${userData.first_name[0]}${userData.last_name[0]}`.toUpperCase()
            : (userData.email ?? '??').substring(0, 2).toUpperCase())
        : (profile.initials || '??');

    const mentorshipRole = userData?.profile?.role;
    const backgroundFallback = userData?.profile?.background
        ? (userData.profile.background.length > 25
            ? userData.profile.background.substring(0, 22) + '...'
            : userData.profile.background)
        : null;
    const userRole = (mentorshipRole && ROLE_LABELS[mentorshipRole])
        || backgroundFallback
        || profile.role
        || 'Chiefy Member';

    // ── Context menu outside-click close ──────────────────────────────

    useEffect(() => {
        if (!contextMenu) return;
        const close = (e: Event) => {
            if (e instanceof MouseEvent) {
                const target = e.target as HTMLElement;
                if (target?.closest('.context-menu') || target?.closest('.chat-more-btn')) return;
            }
            setContextMenu(null);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('scroll', close, true);
        };
    }, [contextMenu]);

    // ── Helpers ──────────────────────────────────────────────────────

    const closeSidebarOnMobile = () => {
        if (window.innerWidth <= 768 && !collapsed) onToggleCollapse();
    };

    const handleMoreBtnClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        type: ContextMenuTarget['type'], id: string, projectId?: string,
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuWidth = 160;
        const menuHeight = 84;
        const x = rect.right - menuWidth;
        const y = rect.bottom + 4 + menuHeight > window.innerHeight
            ? rect.top - menuHeight - 4
            : rect.bottom + 4;
        setContextMenu({ x, y, type, id, projectId });
    };

    const handleContextMenuDelete = () => {
        if (!contextMenu) return;
        setContextMenu(null);
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from ContextMenu union (infrastructure id), not user copy.
        if (contextMenu.type === 'project') {
            setConfirmDeleteProjectId(contextMenu.id);
        } else if (contextMenu.type === 'direct-chat') {
            setConfirmDeleteChatId(contextMenu.id);
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from ContextMenu union (infrastructure id), not user copy.
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            setConfirmDeleteProjectChatId({
                projectId: contextMenu.projectId,
                chatId: contextMenu.id,
            });
        }
    };

    const handleContextMenuRename = () => {
        if (!contextMenu) return;
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from ContextMenu union (infrastructure id), not user copy.
        if (contextMenu.type === 'project') {
            const currentName = data.projects.find((p) => p.id === contextMenu.id)?.name || '';
            setContextMenu(null);
            setRenamingProjectId(contextMenu.id);
            setRenameProjectValue(currentName);
            return;
        }
        const chatId = contextMenu.id;
        let currentName = '';
        if (contextMenu.type === 'direct-chat') {
            currentName = data.directChats.find((c) => c.id === chatId)?.name || '';
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from ContextMenu union (infrastructure id), not user copy.
        } else if (contextMenu.type === 'project-chat' && contextMenu.projectId) {
            const proj = data.projects.find((p) => p.id === contextMenu.projectId);
            currentName = proj?.chats.find((c) => c.id === chatId)?.name || '';
        }
        setContextMenu(null);
        setRenamingChatId(chatId);
        setRenameValue(currentName);
    };

    const executeRenameProject = async (projectId: string) => {
        await data.renameProjectById(projectId, renameProjectValue);
        setRenamingProjectId(null);
    };

    const executeRenameChat = async (chatId: string, projectId?: string) => {
        await data.renameChatById(chatId, renameValue, projectId);
        setRenamingChatId(null);
    };

    const handleCreateChat = (projectId: string) => {
        onTabChange('assistant');
        onChatSelect?.(projectId, '');
    };

    const handleMainScroll: UIEventHandler<HTMLDivElement> = (e) => {
        if (!isYourChatsExpanded) return;
        if (!data.directChatsMoreUrl || data.isLoadingMoreDirectChats) return;
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (distanceFromBottom < 160) void data.loadMoreDirectChats();
    };

    const showTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTooltip({ top: rect.top + rect.height / 2, left: rect.right + 12 });
    };
    const hideTooltip = () => setTooltip(null);
    const handleToggle = () => { hideTooltip(); onToggleCollapse(); };

    const toggleAssistantMenu = () => {
        setExpandedMenus((prev) => {
            const next = new Set(prev);
            if (next.has('assistant')) next.delete('assistant');
            else next.add('assistant');
            return next;
        });
    };

    // ── Shared derived lists for search modal ────────────────────────

    const sq = search.searchQuery.toLowerCase().trim();
    const filteredProjects = useMemo<Project[]>(() => (
        sq
            ? data.projects.filter((p) =>
                p.name.toLowerCase().includes(sq)
                || p.chats.some((c) => c.name?.toLowerCase().includes(sq)))
            : data.projects
    ), [data.projects, sq]);

    const allRecentChats = useMemo(() => (
        [
            ...data.directChats.map((c) => ({
                id: c.id, name: c.name, updatedAt: '',
                projectId: '', projectName: '',
            })),
            ...data.projects.flatMap((p) => p.chats.map((c) => ({
                id: c.id, name: c.name, updatedAt: c.updatedAt || '',
                projectId: p.id, projectName: p.name,
            }))),
        ]
            .filter((c) => !sq
                || c.name?.toLowerCase().includes(sq)
                || c.projectName?.toLowerCase().includes(sq))
            .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
    ), [data.directChats, data.projects, sq]);

    const assistantSubmenu = (
        <AssistantSubmenu
            selectedChatId={selectedChatId}
            selectedProjectId={selectedProjectId}
            projects={data.projects}
            directChats={data.directChats}
            searchQuery={search.searchQuery}
            isProjectsExpanded={isProjectsExpanded}
            isYourChatsExpanded={isYourChatsExpanded}
            expandedProjects={data.expandedProjects}
            animatingChatIds={data.animatingChatIds}
            renamingProjectId={renamingProjectId}
            renameProjectValue={renameProjectValue}
            renamingChatId={renamingChatId}
            renameValue={renameValue}
            isLoadingMoreDirectChats={data.isLoadingMoreDirectChats}
            onNewDirectChat={onNewDirectChat}
            onChatSelect={onChatSelect}
            onOpenSearch={() => search.setSearchOpen(true)}
            onToggleProjects={() => setIsProjectsExpanded((v) => !v)}
            onToggleRecentChats={() => setIsYourChatsExpanded((v) => !v)}
            onToggleProject={data.toggleProject}
            onAddProject={() => data.setShowCreateProjectModal(true)}
            onMoreClick={handleMoreBtnClick}
            onCreateProjectChat={handleCreateChat}
            onRenameProjectChange={setRenameProjectValue}
            onCommitRenameProject={executeRenameProject}
            onCancelRenameProject={() => setRenamingProjectId(null)}
            onRenameChatChange={setRenameValue}
            onCommitRenameChat={executeRenameChat}
            onCancelRenameChat={() => setRenamingChatId(null)}
            onAnimationDone={data.clearAnimation}
            onScroll={handleMainScroll}
            closeSidebarOnMobile={closeSidebarOnMobile}
        />
    );

    return (
        <>
            <aside className={`sidebar ${isOpen ? 'open' : 'closed'} ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <div className="brand">
                        <div
                            className="brand-logo"
                            onMouseEnter={collapsed ? showTooltip : undefined}
                            onMouseLeave={collapsed ? hideTooltip : undefined}
                        >
                            <img src={logo} alt="Chiefy Logo" className="logo-image" />
                            {collapsed && (
                                <button
                                    type="button"
                                    className="sidebar-toggle-overlay"
                                    onClick={handleToggle}
                                    aria-label="Open sidebar"
                                    title="Open sidebar"
                                >
                                    <PanelLeft size={18} />
                                </button>
                            )}
                        </div>
                        {!collapsed && (
                            <div className="brand-text">
                                <img src={logoText} alt="Chiefy" className="logo-text-image" />
                            </div>
                        )}
                    </div>
                    {!collapsed && (
                        <button
                            type="button"
                            className="icon-btn sidebar-toggle"
                            onClick={handleToggle}
                            title="Close sidebar"
                        >
                            <PanelLeft size={18} />
                        </button>
                    )}
                </div>

                {primaryRailEnabled ? (
                    <SidebarPrimaryAdapter
                        activeKey={tabToRailKey(activeTab)}
                        onSelect={(key) => onTabChange(railKeyToTab(key))}
                        collapsed={collapsed}
                        data-testid="sidebar-primary-adapter"
                    />
                ) : (
                    <NavGroups
                        activeTab={activeTab}
                        collapsed={collapsed}
                        userData={userData}
                        networkingSubTab={networkingSubTab}
                        assistantExpanded={expandedMenus.has('assistant')}
                        onTabChange={onTabChange}
                        onToggleCollapse={onToggleCollapse}
                        onToggleAssistantMenu={toggleAssistantMenu}
                        onCollapseAllMenus={() => setExpandedMenus(new Set())}
                        onCloseSidebarOnMobile={closeSidebarOnMobile}
                        assistantSubmenu={assistantSubmenu}
                    />
                )}

                <SidebarFooter
                    ref={setUserMenuAnchor}
                    userData={userData}
                    displayName={displayName}
                    initials={initials}
                    userRole={userRole}
                    activeTab={activeTab}
                    collapsed={collapsed}
                    userMenuOpen={userMenuOpen}
                    onProfileTab={() => { onTabChange('profile'); closeSidebarOnMobile(); }}
                    onOpenMenu={() => setUserMenuOpen((v) => !v)}
                    onUpgrade={() => {
                        onTabChange('profile');
                        closeSidebarOnMobile();
                        setTimeout(() => emitAppEvent('open-plans', { reason: 'menu' }), 100);
                    }}
                />

                <UserMenu
                    anchor={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={() => setUserMenuOpen(false)}
                    user={userData ?? null}
                    onProfile={() => { onTabChange('profile'); closeSidebarOnMobile(); }}
                    onAccountSettings={() => { onTabChange('settings'); closeSidebarOnMobile(); }}
                    onBilling={() => {
                        onTabChange('settings');
                        if (typeof window !== 'undefined') {
                            window.location.hash = '#billing';
                        }
                        closeSidebarOnMobile();
                    }}
                />
            </aside>

            {tooltip && (
                <div
                    className="sidebar-icon-tooltip"
                    style={{ top: tooltip.top, left: tooltip.left }}
                >
                    {collapsed ? 'Open sidebar' : 'Close sidebar'}
                </div>
            )}

            <div
                className={`sidebar-edge-zone ${collapsed ? 'sidebar-edge-zone--collapsed' : ''}`}
                onClick={handleToggle}
            />

            {contextMenu && (
                <ContextMenu
                    target={contextMenu}
                    onEditProject={() => {
                        setEditingProjectId(contextMenu.id);
                        setContextMenu(null);
                    }}
                    onRename={handleContextMenuRename}
                    onDelete={handleContextMenuDelete}
                />
            )}

            <ConfirmDeleteModals
                directChats={data.directChats}
                projects={data.projects}
                confirmDeleteChatId={confirmDeleteChatId}
                confirmDeleteProjectId={confirmDeleteProjectId}
                confirmDeleteProjectChatId={confirmDeleteProjectChatId}
                isDeletingChatId={data.isDeletingChatId}
                isDeletingProjectId={data.isDeletingProjectId}
                isDeletingProjectChatId={data.isDeletingProjectChatId}
                onCancelChat={() => setConfirmDeleteChatId(null)}
                onCancelProject={() => setConfirmDeleteProjectId(null)}
                onCancelProjectChat={() => setConfirmDeleteProjectChatId(null)}
                onConfirmChat={async (id) => {
                    await data.deleteDirectChat(id);
                    setConfirmDeleteChatId(null);
                }}
                onConfirmProject={async (id) => {
                    await data.deleteProject(id);
                    setConfirmDeleteProjectId(null);
                }}
                onConfirmProjectChat={async (pid, cid) => {
                    await data.deleteProjectChat(pid, cid);
                    setConfirmDeleteProjectChatId(null);
                }}
            />

            <SearchModal
                open={search.searchOpen}
                searchQuery={search.searchQuery}
                onSearchChange={search.handleSearchChange}
                onClose={search.close}
                apiResults={search.apiSearchResults}
                isSearching={search.isSearching}
                onLoadMore={search.loadMore}
                projects={data.projects}
                filteredProjects={filteredProjects}
                allRecentChats={allRecentChats}
                onOpenProject={(projectId) => {
                    onTabChange('assistant');
                    data.toggleProject(projectId);
                }}
                onSelectChat={(projectId, chatId) => {
                    onChatSelect?.(projectId, chatId);
                    closeSidebarOnMobile();
                }}
            />

            {data.showCreateProjectModal && (
                <ProjectEditModal
                    mode="create"
                    onClose={() => data.setShowCreateProjectModal(false)}
                    onSaved={(created) => {
                        const projectWithChats: Project = {
                            id: created.id,
                            name: created.name,
                            description: created.description,
                            files: created.files,
                            chats: [],
                        };
                        data.setProjects((prev) => [projectWithChats, ...prev]);
                        data.expandProject(created.id);
                    }}
                />
            )}

            {editingProjectId && (() => {
                const proj = data.projects.find((p) => p.id === editingProjectId);
                if (!proj) return null;
                return (
                    <ProjectEditModal
                        mode="edit"
                        projectId={proj.id}
                        name={proj.name}
                        description={proj.description || ''}
                        files={proj.files || []}
                        phase={proj.phase}
                        stage={proj.stage}
                        onClose={() => setEditingProjectId(null)}
                        onSaved={(updated) => {
                            data.setProjects((prev) => prev.map((p) =>
                                p.id === editingProjectId
                                    ? { ...p, name: updated.name, description: updated.description, files: updated.files, phase: updated.phase, stage: updated.stage }
                                    : p,
                            ));
                        }}
                    />
                );
            })()}
        </>
    );
}
